import { useRouter } from 'expo-router';
import { SearchIcon } from 'lucide-react-native';
import { useLayoutEffect } from 'react';
import { twJoin } from 'tailwind-merge';

import { Badge, BadgeText } from '@/components/ui/badge';
import { Button, ButtonIcon, ButtonText } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { ROUTES } from '@/constants/routes';
import { useAbility } from '@/hooks/useAbility';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { trackLibraryChatEntryTriggered } from '@/util/analyticsEvents';

function SignedInEntry({ fullWidth }: { fullWidth: boolean }) {
  const router = useRouter();

  const ability = useAbility();

  if (!ability.can('read', 'AskConversation')) {
    return null;
  }

  return (
    <VStack className="gap-2 rounded-md border border-outline-200 bg-background-0 p-4">
      <Badge action="info" size="md" className="self-start">
        <BadgeText className="normal-case text-info-700">Beta</BadgeText>
      </Badge>

      <Text role="heading" aria-level={2} className="font-bold text-base text-typography-900">
        Find inquiries like your patient&apos;s case
      </Text>

      <Text className="text-sm text-typography-700">
        Describe a case or research question in your own words. We&apos;ll match answered inquiries
        from specialists.
      </Text>

      <Button
        variant="solid"
        action="secondary"
        className={twJoin('mt-2 w-full', !fullWidth && 'md:w-auto md:self-start')}
        onPress={() => {
          trackLibraryChatEntryTriggered();

          router.push(ROUTES.LIBRARY_CHAT);
        }}
        accessibilityHint="Opens Research, which takes a question or a case in your own words">
        <ButtonIcon as={SearchIcon} />
        <ButtonText>Try Research</ButtonText>
      </Button>
    </VStack>
  );
}

/**
 * The Inquiry Library's way into Research, as a card that says what the beta
 * does rather than a bare button. Shown only to a reader who would get past
 * both the route's guard and its ability check, so the card never leads to a
 * refusal.
 *
 * The ability is read in a child that mounts only for a signed-in, verified
 * reader. The Inquiry Library also renders in the open group, where no ability
 * provider exists; reading the ability there reports a missing provider to
 * Sentry on every render, which a signed-out visitor typing a keyword search
 * does once per keystroke.
 */
export function AskTheLibraryEntry({ fullWidth = false }: { fullWidth?: boolean }) {
  const isVisible = useAskTheLibraryEntryVisible();

  if (!isVisible) {
    return null;
  }

  return <SignedInEntry fullWidth={fullWidth} />;
}

/**
 * Whether the entry would render for this reader, apart from the ability check it makes once
 * mounted. Lets a layout know before it reserves room for the card.
 */
export function useAskTheLibraryEntryVisible() {
  const isEnabled = useFeatureFlag(FEATURE_FLAGS.LIBRARY_CHAT);

  const { isAuthenticated, needsNpiVerification } = useAuthStatus();

  return isEnabled && isAuthenticated && !needsNpiVerification;
}

/**
 * Reports whether the signed-in reader holds the ability the card needs, for a layout that must
 * know before it reserves room. Render it only where `useAskTheLibraryEntryVisible` is true: it
 * reads the ability, which has no provider for a signed-out visitor. Renders nothing.
 */
export function ResearchAbilityProbe({ onResolve }: { onResolve: (canRead: boolean) => void }) {
  const ability = useAbility();

  const canRead = ability.can('read', 'AskConversation');

  useLayoutEffect(() => {
    onResolve(canRead);

    return () => onResolve(false);
  }, [canRead, onResolve]);

  return null;
}
